// #55 Row and RowName: the planner row (.mc-prow), a grid of `cols` equal tracks (or a `lead` first column of a given
// percentage and equal tracks after it), 44–56 px tall, a hairline between rows. `head` is the 11.5 px uppercase head
// row on --card-2; `off` dims the row's name (a round not in use). A row that opens something is one <a> (href) or
// <button> (onOpen) and lifts on hover, so it must hold no other control. `loading`: skeleton cells at the row's
// geometry. Cells are spans (a row may be a button). Workbench, approvals and worklist rows are compositions of this one.
// as="li" puts the row in a list: a plain row is the <li>; an opening row's link or button sits in an <li>.
// A plain row that opens something AND holds another control (an action button) links its name instead: RowName
// `href` makes the bold name the row's one link, so the sibling button never nests inside an <a>.
// `tracks` (DC-235, F-150): the row takes a list of named tracks instead of equal ones, so a list sizes a pill column by
// its pill — `fit` (auto: the pill never wraps and its column never shrinks below it) — and its text columns by share
// (`lead` minmax(180px, 2fr), `wide` minmax(120px, 1.2fr), `text` minmax(0, 1fr)). Its cells sit in one inner grid and
// the row is the container a narrow host is measured by: with `lines`, under 820 px of row the cells draw on two lines
// in the order given (the first column the text, the others content-sized), so two pills never share a cell or overlap.

import type { MouseEvent, ReactElement, ReactNode } from "react";
import { Children } from "react";
import { cx } from "../lib/cx";
import { fullTitle } from "../lib/textOf";
import { useHs, type Hs } from "../lib/hs";
import { vars } from "../lib/vars";
import { Skeleton } from "./Skeleton";

/** A named track (DC-235): the row's name, a second text column, an equal text column, or a column sized by its content. */
export type RowTrack = "lead" | "wide" | "text" | "fit";

/** The most tracks a row names (the CSS writes one template per count). */
export const ROW_MAX_TRACKS = 8;

export interface RowProps {
  /** One child per column. */
  children?: ReactNode;
  /** Named tracks, one per column (DC-235); replaces `cols` and `lead`. */
  tracks?: readonly RowTrack[] | undefined;
  /** With `tracks`: the cells' columns, line by line, where the row is narrower than 820 px (e.g. [[0, 3, 5], [1, 2, 4]]). */
  lines?: readonly (readonly number[])[] | undefined;
  /** The column count (default: the children). */
  cols?: number | undefined;
  /** The first column's width as a percentage of the row; the rest share what remains. */
  lead?: `${number}%` | undefined;
  head?: boolean | undefined;
  /** A head row's cell titles, by column, for a cell whose words come from props (a Person: its name and sub line, both cut
   * short in a head cell) rather than its children. */
  titles?: readonly (string | undefined)[] | undefined;
  off?: boolean | undefined;
  href?: string | undefined;
  onOpen?: ((event: MouseEvent<HTMLElement>) => void) | undefined;
  loading?: boolean | undefined;
  /** The opening row's accessible name, when its cells' words are not enough. */
  label?: string | undefined;
  /** The row's element: "li" when the host is a <ul> or <ol> (default "div"). */
  as?: "div" | "li" | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

export function Row(props: RowProps) {
  return props.tracks && props.tracks.length > 0 ? <TrackRow {...props} tracks={props.tracks} /> : <EqualRow {...props} />;
}

/** Where each cell sits on the narrow two-line layout: its line and its column (1-based), by cell index. */
function placements(lines: readonly (readonly number[])[] | undefined): Map<number, { row: number; col: number }> {
  const placed = new Map<number, { row: number; col: number }>();
  (lines ?? []).forEach((line, row) => line.forEach((cell, col) => placed.set(cell, { row: row + 1, col: col + 1 })));
  return placed;
}

function TrackRow({ children, tracks, lines, head, titles, off, href, onOpen, loading, label, as = "div", hs, className }: RowProps & { tracks: readonly RowTrack[] }) {
  const hot = useHs(hs);
  if (tracks.length > ROW_MAX_TRACKS) throw new Error(`Row: at most ${ROW_MAX_TRACKS} tracks`);
  const count = tracks.length;
  const placed = placements(lines);
  const narrowCols = Math.max(1, ...(lines ?? []).map((line) => line.length));
  const trackVars: Record<`--mc-${string}`, `var(--${string})`> = {};
  tracks.forEach((track, index) => { trackVars[`--mc-t${index + 1}`] = `var(--mc-track-${track})`; });
  const gridStyle = vars({ ...trackVars, "--mc-ncols": lines ? narrowCols - 1 : undefined });
  const classes = cx(
    "mc-prow", "mc-prow-tracks", head && "mc-prow-head", off && "mc-prow-off", (href !== undefined || onOpen) && "mc-prow-link",
    loading && "mc-prow-skel", className,
  );
  const cellOf = (index: number, content: ReactNode, title?: string) => {
    const at = placed.get(index);
    return (
      <span
        key={index}
        className={cx("mc-prow-c", tracks[index] === "fit" && "mc-prow-fit")}
        style={at ? vars({ "--mc-nr": at.row, "--mc-nc": at.col }) : undefined}
        title={title}
      >
        {content}
      </span>
    );
  };
  const cells = loading
    ? Array.from({ length: count }, (_, index) => cellOf(index, <Skeleton width={index === 0 ? "80%" : "60%"} />))
    : Children.map(children, (child, index) => cellOf(index, child, head ? titles?.[index] ?? fullTitle(child) : undefined));
  const grid = <span className={cx("mc-prow-g", `mc-prow-t${count}`, lines && "mc-prow-lines")} style={gridStyle}>{cells}</span>;
  const Plain = as;
  if (loading) return <Plain className={classes} aria-busy="true">{grid}</Plain>;
  const item = (control: ReactElement) => (as === "li" ? <li className="mc-prow-li">{control}</li> : control);
  if (href !== undefined) return item(<a href={href} className={classes} aria-label={label} onClick={onOpen} {...hot}>{grid}</a>);
  if (onOpen) return item(<button type="button" className={classes} aria-label={label} onClick={onOpen} {...hot}>{grid}</button>);
  return <Plain className={classes} {...hot}>{grid}</Plain>;
}

function EqualRow({ children, cols, lead, head, titles, off, href, onOpen, loading, label, as = "div", hs, className }: RowProps) {
  const hot = useHs(hs);
  const count = cols ?? (Children.count(children) || 1);
  const style = vars({ "--mc-cols": lead ? Math.max(1, count - 1) : count, "--mc-lead": lead });
  const classes = cx(
    "mc-prow", lead && "mc-prow-lead", head && "mc-prow-head", off && "mc-prow-off", (href !== undefined || onOpen) && "mc-prow-link",
    loading && "mc-prow-skel", className,
  );
  const cells = loading
    ? Array.from({ length: count }, (_, index) => <span key={index} className="mc-prow-c"><Skeleton width={index === 0 ? "80%" : "60%"} /></span>)
    : Children.map(children, (child, index) => <span className="mc-prow-c" title={head ? titles?.[index] ?? fullTitle(child) : undefined}>{child}</span>);
  const Plain = as;
  if (loading) return <Plain className={classes} style={style} aria-busy="true">{cells}</Plain>;
  const item = (control: ReactElement) => (as === "li" ? <li className="mc-prow-li">{control}</li> : control);
  if (href !== undefined) {
    return item(<a href={href} className={classes} style={style} aria-label={label} onClick={onOpen} {...hot}>{cells}</a>);
  }
  if (onOpen) {
    return item(<button type="button" className={classes} style={style} aria-label={label} onClick={onOpen} {...hot}>{cells}</button>);
  }
  return <Plain className={classes} style={style} {...hot}>{cells}</Plain>;
}

export interface RowNameProps {
  name: ReactNode;
  sub?: ReactNode | undefined;
  /** The name as a link (a deep link to the record), for a plain Row that holds another control. */
  href?: string | undefined;
  onClick?: ((event: MouseEvent<HTMLAnchorElement>) => void) | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

/** A row's name: the bold words and a --text-3 sub line. */
export function RowName({ name, sub, href, onClick, hs, className }: RowNameProps) {
  const hot = useHs(href !== undefined ? hs : undefined);
  return (
    <span className={cx("mc-rname", href !== undefined && "mc-rname-link", className)}>
      <b>{href !== undefined ? <a href={href} className="mc-rname-a" onClick={onClick} {...hot}>{name}</a> : name}</b>
      {sub !== undefined && sub !== null ? <span>{sub}</span> : null}
    </span>
  );
}
